import { useEffect, useMemo, useState } from 'react';
import type { User } from '@supabase/supabase-js';
import { IssueCapture } from './components/IssueCapture';
import { IssueRegister } from './components/IssueRegister';
import { SearchCombobox } from './components/SearchCombobox';
import { getInspectionPlan, getInspectionProgress } from './lib/inspection';
import {
  loadInspectionResponses,
  saveInspectionResponse,
  startOrResumeAssessment,
} from './services/inspection';
import {
  getExistingUser,
  getOrCreateGuestUser,
  loadDevelopers,
  loadLatestProperty,
  loadProjects,
  saveProperty,
} from './services/propertySetup';
import type {
  AssessmentRecord,
  InspectionAnswer,
  InspectionCheck,
  InspectionResponseRecord,
} from './types/inspection';
import type {
  Developer,
  Project,
  PropertyDraft,
  PropertyType,
  SavedProperty,
} from './types/property';

type View = 'landing' | 'property' | 'saved' | 'inspection' | 'finding' | 'issues';
type AsyncStatus = 'idle' | 'loading' | 'ready' | 'error';

interface PendingFinding {
  response: InspectionResponseRecord;
  check: InspectionCheck;
}

const propertyOptions: Array<{
  id: PropertyType;
  title: string;
  description: string;
}> = [
  {
    id: 'apartment',
    title: 'Apartment',
    description: 'A flat in a gated community or multi-tower residential project.',
  },
  {
    id: 'villa',
    title: 'Villa',
    description: 'A villa inside a managed gated community.',
  },
];

const answerOptions: Array<{
  value: InspectionAnswer;
  label: string;
  description: string;
}> = [
  { value: 'looks_good', label: 'Looks good', description: 'No visible concern from this guided check.' },
  { value: 'issue_found', label: 'Issue found', description: 'Record this as a finding for follow-up.' },
  { value: 'not_sure', label: 'Not sure', description: 'Keep this visible for later review or expert help.' },
  { value: 'cannot_check', label: 'Cannot check', description: 'Access, utilities or site conditions prevent a safe check.' },
  { value: 'not_applicable', label: 'Not applicable', description: 'This item does not apply to the unit as delivered.' },
];

const createEmptyDraft = (): PropertyDraft => ({
  propertyType: 'apartment',
  developerId: null,
  projectId: null,
  manualDeveloperName: '',
  manualProjectName: '',
  locality: '',
  towerOrBlock: '',
  unitOrVillaNumber: '',
  bedrooms: 3,
  bathrooms: 2,
  inspectionStage: 'pre_handover',
});

function savedPropertyToDraft(property: SavedProperty): PropertyDraft {
  return {
    propertyType: property.property_type,
    developerId: property.developer_id,
    projectId: property.project_id,
    manualDeveloperName: property.manual_developer_name ?? '',
    manualProjectName: property.manual_project_name ?? '',
    locality: property.locality ?? '',
    towerOrBlock: property.tower_or_block ?? '',
    unitOrVillaNumber: property.unit_or_villa_number ?? '',
    bedrooms: property.bedrooms ?? 3,
    bathrooms: property.bathrooms ?? 2,
    inspectionStage: property.inspection_stage,
  };
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }

  return 'Something went wrong. Please retry.';
}

export default function App() {
  const [view, setView] = useState<View>('landing');
  const [draft, setDraft] = useState<PropertyDraft>(createEmptyDraft);
  const [user, setUser] = useState<User | null>(null);
  const [savedProperty, setSavedProperty] = useState<SavedProperty | null>(null);
  const [existingPropertyId, setExistingPropertyId] = useState<string | null>(null);
  const [developers, setDevelopers] = useState<Developer[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [manualMode, setManualMode] = useState(false);
  const [authStatus, setAuthStatus] = useState<AsyncStatus>('idle');
  const [directoryStatus, setDirectoryStatus] = useState<AsyncStatus>('idle');
  const [projectStatus, setProjectStatus] = useState<AsyncStatus>('idle');
  const [saveStatus, setSaveStatus] = useState<AsyncStatus>('idle');
  const [inspectionStatus, setInspectionStatus] = useState<AsyncStatus>('idle');
  const [responseStatus, setResponseStatus] = useState<AsyncStatus>('idle');
  const [assessment, setAssessment] = useState<AssessmentRecord | null>(null);
  const [responses, setResponses] = useState<Record<string, InspectionResponseRecord>>({});
  const [activeCheckIndex, setActiveCheckIndex] = useState(0);
  const [pendingFinding, setPendingFinding] = useState<PendingFinding | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [isOnline, setIsOnline] = useState(() => navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function restoreSession() {
      try {
        const existingUser = await getExistingUser();

        if (!existingUser || cancelled) {
          return;
        }

        setUser(existingUser);
        const property = await loadLatestProperty(existingUser.id);

        if (!cancelled && property) {
          setSavedProperty(property);
        }
      } catch {
        // A missing or expired session should not block the public landing page.
      }
    }

    void restoreSession();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function refreshProjects() {
      if (!draft.developerId || manualMode) {
        setProjects([]);
        setProjectStatus('idle');
        return;
      }

      setProjectStatus('loading');
      setMessage(null);

      try {
        const nextProjects = await loadProjects(draft.developerId, draft.propertyType);

        if (!cancelled) {
          setProjects(nextProjects);
          setProjectStatus('ready');
        }
      } catch (error) {
        if (!cancelled) {
          setProjectStatus('error');
          setMessage(getErrorMessage(error));
        }
      }
    }

    void refreshProjects();

    return () => {
      cancelled = true;
    };
  }, [draft.developerId, draft.propertyType, manualMode]);

  const developerOptions = useMemo(
    () =>
      developers.map((developer) => ({
        id: developer.id,
        label: developer.display_name,
        aliases: developer.aliases,
      })),
    [developers],
  );

  const projectOptions = useMemo(
    () =>
      projects.map((project) => ({
        id: project.id,
        label: project.project_name,
        aliases: project.aliases,
        context: project.locality,
      })),
    [projects],
  );

  const inspectionChecks = useMemo(
    () => (savedProperty ? getInspectionPlan(savedProperty) : []),
    [savedProperty],
  );
  const inspectionProgress = useMemo(
    () => getInspectionProgress(inspectionChecks, responses),
    [inspectionChecks, responses],
  );
  const activeCheck = inspectionChecks[activeCheckIndex] ?? null;

  const canSave = manualMode
    ? draft.manualDeveloperName.trim().length >= 2 &&
      draft.manualProjectName.trim().length >= 2 &&
      draft.unitOrVillaNumber.trim().length >= 1
    : Boolean(
        draft.developerId &&
          draft.projectId &&
          draft.unitOrVillaNumber.trim().length >= 1,
      );

  async function ensureDirectory(): Promise<void> {
    if (developers.length > 0 || directoryStatus === 'loading') {
      return;
    }

    setDirectoryStatus('loading');

    try {
      const nextDevelopers = await loadDevelopers();
      setDevelopers(nextDevelopers);
      setDirectoryStatus('ready');
    } catch (error) {
      setDirectoryStatus('error');
      setMessage(getErrorMessage(error));
    }
  }

  async function startGuestSetup(resume: boolean): Promise<void> {
    if (!isOnline) {
      setMessage('Connect to the internet to start or restore a secure guest session.');
      return;
    }

    setAuthStatus('loading');
    setMessage(null);

    try {
      const guestUser = user ?? (await getOrCreateGuestUser());
      setUser(guestUser);
      await ensureDirectory();

      const latest = savedProperty ?? (await loadLatestProperty(guestUser.id));

      if (resume && latest) {
        setSavedProperty(latest);
        setExistingPropertyId(latest.id);
        setDraft(savedPropertyToDraft(latest));
        setManualMode(!latest.project_id);
      } else {
        setExistingPropertyId(null);
        setDraft(createEmptyDraft());
        setManualMode(false);
      }

      setAuthStatus('ready');
      setView('property');
    } catch (error) {
      setAuthStatus('error');
      setMessage(getErrorMessage(error));
    }
  }

  async function handleSave(): Promise<void> {
    if (!user || !canSave || !isOnline) {
      setMessage(
        !isOnline
          ? 'You are offline. Reconnect before saving this property.'
          : 'Complete the required property details before saving.',
      );
      return;
    }

    setSaveStatus('loading');
    setMessage(null);

    try {
      const property = await saveProperty(user.id, draft, existingPropertyId);
      setSavedProperty(property);
      setExistingPropertyId(property.id);
      setSaveStatus('ready');
      setView('saved');
    } catch (error) {
      setSaveStatus('error');
      setMessage(getErrorMessage(error));
    }
  }

  async function handleStartInspection(): Promise<void> {
    if (!user || !savedProperty || !isOnline) {
      setMessage(
        !isOnline
          ? 'Reconnect to start or resume the inspection securely.'
          : 'A saved property and secure guest session are required.',
      );
      return;
    }

    setInspectionStatus('loading');
    setMessage(null);

    try {
      const nextAssessment = await startOrResumeAssessment(user.id, savedProperty.id);
      const nextResponses = await loadInspectionResponses(user.id, nextAssessment.id);
      const firstUnanswered = inspectionChecks.findIndex((item) => !nextResponses[item.id]);

      setAssessment(nextAssessment);
      setResponses(nextResponses);
      setActiveCheckIndex(firstUnanswered >= 0 ? firstUnanswered : 0);
      setInspectionStatus('ready');
      setView('inspection');
    } catch (error) {
      setInspectionStatus('error');
      setMessage(getErrorMessage(error));
    }
  }

  function advanceFromCurrent(nextResponses: Record<string, InspectionResponseRecord>) {
    const nextIndex = inspectionChecks.findIndex(
      (item, index) => index > activeCheckIndex && !nextResponses[item.id],
    );

    if (nextIndex >= 0) {
      setActiveCheckIndex(nextIndex);
    }
  }

  async function handleAnswer(answer: InspectionAnswer): Promise<void> {
    if (!user || !assessment || !activeCheck || !isOnline) {
      setMessage(
        !isOnline
          ? 'You are offline. Reconnect before saving this check.'
          : 'The inspection session is not ready yet.',
      );
      return;
    }

    setResponseStatus('loading');
    setMessage(null);

    try {
      const response = await saveInspectionResponse(
        user.id,
        assessment.id,
        activeCheck,
        answer,
      );
      const nextResponses = { ...responses, [activeCheck.id]: response };
      setResponses(nextResponses);
      setResponseStatus('ready');

      if (answer === 'issue_found' || answer === 'not_sure') {
        setPendingFinding({ response, check: activeCheck });
        setView('finding');
        return;
      }

      advanceFromCurrent(nextResponses);
    } catch (error) {
      setResponseStatus('error');
      setMessage(getErrorMessage(error));
    }
  }

  if (
    view === 'finding' &&
    user &&
    assessment &&
    pendingFinding
  ) {
    return (
      <div className="app-shell inspection-shell">
        <header className="app-header">
          <button
            className="icon-button"
            type="button"
            aria-label="Back to guided check"
            onClick={() => setView('inspection')}
          >
            ←
          </button>
          <div className="header-copy">
            <strong>Capture finding</strong>
            <span>Private guest workspace</span>
          </div>
          <span className={`connection-pill ${isOnline ? '' : 'connection-pill-offline'}`}>
            {isOnline ? 'Online' : 'Offline'}
          </span>
        </header>
        <IssueCapture
          ownerId={user.id}
          assessmentId={assessment.id}
          response={pendingFinding.response}
          check={pendingFinding.check}
          online={isOnline}
          onBack={() => setView('inspection')}
          onDone={() => {
            setPendingFinding(null);
            advanceFromCurrent(responses);
            setView('inspection');
          }}
        />
      </div>
    );
  }

  if (view === 'issues' && user && assessment) {
    return (
      <div className="app-shell inspection-shell">
        <header className="app-header">
          <button
            className="icon-button"
            type="button"
            aria-label="Back to guided inspection"
            onClick={() => setView('inspection')}
          >
            ←
          </button>
          <div className="header-copy">
            <strong>Issue register</strong>
            <span>Private findings and evidence</span>
          </div>
          <span className="scope-pill">Hyderabad alpha</span>
        </header>
        <IssueRegister
          ownerId={user.id}
          assessmentId={assessment.id}
          onBack={() => setView('inspection')}
        />
      </div>
    );
  }

  if (view === 'inspection' && savedProperty && assessment && activeCheck) {
    const currentResponse = responses[activeCheck.id];

    return (
      <div className="app-shell inspection-shell">
        <header className="app-header">
          <button
            className="icon-button"
            type="button"
            aria-label="Back to property workspace"
            onClick={() => setView('saved')}
          >
            ←
          </button>
          <div className="header-copy">
            <strong>Guided inspection</strong>
            <span>{inspectionProgress.answered} of {inspectionProgress.total} checks saved</span>
          </div>
          <span className={`connection-pill ${isOnline ? '' : 'connection-pill-offline'}`}>
            {isOnline ? 'Online' : 'Offline'}
          </span>
        </header>

        <main className="page inspection-page">
          <section className="inspection-progress-card" aria-label="Inspection progress">
            <div className="inspection-progress-copy">
              <span className="eyebrow">Inspection progress</span>
              <strong>{inspectionProgress.percent}% complete</strong>
              <span>{savedProperty.property_type === 'apartment' ? 'Apartment' : 'Villa'} plan, {inspectionProgress.total} contextual checks</span>
            </div>
            <div className="progress-track" aria-hidden="true">
              <span style={{ width: `${inspectionProgress.percent}%` }} />
            </div>
            <button className="text-button issue-register-link" type="button" onClick={() => setView('issues')}>
              Open issue register
            </button>
          </section>

          {message ? <div className="status-banner" role="alert">{message}</div> : null}

          <section className="inspection-card">
            <div className="inspection-card-topline">
              <span className={`severity-chip severity-${activeCheck.severity}`}>
                {activeCheck.severity}
              </span>
              <span className="check-position">Check {activeCheckIndex + 1} of {inspectionChecks.length}</span>
            </div>
            <span className="eyebrow">{activeCheck.category} · {activeCheck.area}</span>
            <h1>{activeCheck.title}</h1>
            <p className="lead">{activeCheck.prompt}</p>

            <div className="guidance-grid">
              <article className="guidance-card guidance-good">
                <strong>Good sign</strong>
                <p>{activeCheck.goodSign}</p>
              </article>
              <article className="guidance-card guidance-warning">
                <strong>Warning sign</strong>
                <p>{activeCheck.warningSign}</p>
              </article>
            </div>

            <div className="expert-boundary">
              <strong>Safety boundary</strong>
              <p>{activeCheck.expertBoundary}</p>
            </div>

            <div className="answer-grid" aria-label="Inspection response">
              {answerOptions.map((option) => (
                <button
                  key={option.value}
                  className={`answer-card ${currentResponse?.answer === option.value ? 'answer-card-selected' : ''}`}
                  type="button"
                  disabled={responseStatus === 'loading' || !isOnline}
                  onClick={() => void handleAnswer(option.value)}
                >
                  <strong>{option.label}</strong>
                  <span>{option.description}</span>
                </button>
              ))}
            </div>

            <div className="inspection-navigation">
              <button
                className="button button-secondary ink-button"
                type="button"
                disabled={activeCheckIndex === 0}
                onClick={() => setActiveCheckIndex((index) => Math.max(0, index - 1))}
              >
                Previous
              </button>
              <button
                className="button button-secondary ink-button"
                type="button"
                disabled={activeCheckIndex >= inspectionChecks.length - 1}
                onClick={() =>
                  setActiveCheckIndex((index) => Math.min(inspectionChecks.length - 1, index + 1))
                }
              >
                Next
              </button>
            </div>
          </section>

          <p className="inline-note inspection-source-note">
            Guided checks are buyer-observable prompts derived from the approved Buyer Lite inspection catalogue. They do not certify structural, electrical, plumbing or legal compliance.
          </p>
        </main>
      </div>
    );
  }

  if (view === 'saved' && savedProperty) {
    const selectedProject = projects.find(
      (project) => project.id === savedProperty.project_id,
    );

    return (
      <div className="app-shell">
        <header className="app-header">
          <button
            className="icon-button"
            type="button"
            aria-label="Edit property"
            onClick={() => setView('property')}
          >
            ←
          </button>
          <div className="header-copy">
            <strong>Property saved</strong>
            <span>Private guest workspace</span>
          </div>
          <span className="scope-pill">Hyderabad alpha</span>
        </header>

        <main className="page narrow-page">
          <section className="success-card">
            <span className="success-icon" aria-hidden="true">✓</span>
            <span className="eyebrow">Setup saved securely</span>
            <h1>Your property workspace is ready.</h1>
            <p className="lead">
              The property is linked to this anonymous guest account and protected by database access rules.
            </p>

            {message ? <div className="status-banner" role="alert">{message}</div> : null}

            <dl className="property-summary">
              <div>
                <dt>Property</dt>
                <dd>{savedProperty.property_type === 'apartment' ? 'Apartment' : 'Villa'}</dd>
              </div>
              <div>
                <dt>Project</dt>
                <dd>
                  {selectedProject?.project_name ??
                    savedProperty.manual_project_name ??
                    'Selected gated community'}
                </dd>
              </div>
              <div>
                <dt>Unit</dt>
                <dd>{savedProperty.unit_or_villa_number ?? 'Not provided'}</dd>
              </div>
              <div>
                <dt>Stage</dt>
                <dd>{savedProperty.inspection_stage.replaceAll('_', ' ')}</dd>
              </div>
            </dl>

            <div className="hero-actions">
              <button
                className="button button-primary"
                type="button"
                disabled={inspectionStatus === 'loading'}
                onClick={() => void handleStartInspection()}
              >
                {inspectionStatus === 'loading' ? 'Preparing inspection…' : 'Start or resume inspection'}
              </button>
              <button
                className="button button-secondary ink-button"
                type="button"
                onClick={() => setView('property')}
              >
                Edit property
              </button>
            </div>
            <p className="inline-note">
              The inspection plan is generated from the property type, bedroom count and bathroom count. Answers are saved to the secure guest workspace.
            </p>
          </section>
        </main>
      </div>
    );
  }

  if (view === 'property') {
    return (
      <div className="app-shell">
        <header className="app-header">
          <button
            className="icon-button"
            type="button"
            aria-label="Go back"
            onClick={() => setView('landing')}
          >
            ←
          </button>
          <div className="header-copy">
            <strong>Set up your property</strong>
            <span>{isOnline ? 'Secure guest session active' : 'Offline, changes are not saved'}</span>
          </div>
          <span className={`connection-pill ${isOnline ? '' : 'connection-pill-offline'}`}>
            {isOnline ? 'Online' : 'Offline'}
          </span>
        </header>

        <main className="page narrow-page">
          <span className="eyebrow">Property profile</span>
          <h1>What are you taking possession of?</h1>
          <p className="lead">
            NivasaSaathi will show only the checks relevant to the selected gated-community property.
          </p>

          {message ? <div className="status-banner" role="alert">{message}</div> : null}

          <section className="choice-grid" aria-label="Property type">
            {propertyOptions.map((option) => (
              <button
                key={option.id}
                className={`choice-card ${draft.propertyType === option.id ? 'choice-card-selected' : ''}`}
                type="button"
                aria-pressed={draft.propertyType === option.id}
                onClick={() => {
                  setDraft((current) => ({
                    ...current,
                    propertyType: option.id,
                    projectId: null,
                  }));
                }}
              >
                <span className="choice-marker" aria-hidden="true">
                  {draft.propertyType === option.id ? '✓' : ''}
                </span>
                <span>
                  <strong>{option.title}</strong>
                  <small>{option.description}</small>
                </span>
              </button>
            ))}
          </section>

          <section className="form-card" aria-labelledby="community-heading">
            <div>
              <span className="eyebrow">Gated community</span>
              <h2 id="community-heading">Find your developer and project</h2>
              <p>
                Search by developer, project, alias or locality. Missing projects can be submitted for verification without blocking the inspection.
              </p>
            </div>

            {!manualMode ? (
              <>
                <SearchCombobox
                  label="Developer"
                  placeholder={directoryStatus === 'loading' ? 'Loading developers…' : 'Search developer name'}
                  options={developerOptions}
                  value={draft.developerId}
                  disabled={directoryStatus === 'loading'}
                  emptyMessage="No matching verified developer yet. Use manual entry below."
                  onChange={(developerId) => {
                    setDraft((current) => ({
                      ...current,
                      developerId,
                      projectId: null,
                      locality: '',
                    }));
                  }}
                />

                <SearchCombobox
                  label="Project"
                  placeholder={
                    !draft.developerId
                      ? 'Select a developer first'
                      : projectStatus === 'loading'
                        ? 'Loading projects…'
                        : 'Search project or locality'
                  }
                  options={projectOptions}
                  value={draft.projectId}
                  disabled={!draft.developerId || projectStatus === 'loading'}
                  emptyMessage="No matching project for this property type. Use manual entry."
                  onChange={(projectId) => {
                    const project = projects.find((item) => item.id === projectId);
                    setDraft((current) => ({
                      ...current,
                      projectId,
                      locality: project?.locality ?? '',
                    }));
                  }}
                />
              </>
            ) : (
              <div className="manual-grid">
                <label className="field">
                  <span>Developer name *</span>
                  <input
                    value={draft.manualDeveloperName}
                    placeholder="Example: Aparna Constructions"
                    onChange={(event) =>
                      setDraft((current) => ({
                        ...current,
                        manualDeveloperName: event.target.value,
                      }))
                    }
                  />
                </label>
                <label className="field">
                  <span>Project name *</span>
                  <input
                    value={draft.manualProjectName}
                    placeholder="Enter the gated-community project"
                    onChange={(event) =>
                      setDraft((current) => ({
                        ...current,
                        manualProjectName: event.target.value,
                      }))
                    }
                  />
                </label>
              </div>
            )}

            <button
              className="text-button"
              type="button"
              onClick={() => {
                setManualMode((current) => !current);
                setDraft((current) => ({
                  ...current,
                  developerId: null,
                  projectId: null,
                }));
              }}
            >
              {manualMode ? '← Search verified projects' : 'Can’t find your project? Add it manually'}
            </button>
          </section>

          <section className="form-card" aria-labelledby="details-heading">
            <div>
              <span className="eyebrow">Unit details</span>
              <h2 id="details-heading">Details needed for a contextual checklist</h2>
            </div>

            <div className="field-grid">
              <label className="field">
                <span>Locality</span>
                <input
                  value={draft.locality}
                  placeholder="Example: Narsingi"
                  onChange={(event) =>
                    setDraft((current) => ({ ...current, locality: event.target.value }))
                  }
                />
              </label>
              <label className="field">
                <span>{draft.propertyType === 'apartment' ? 'Tower or block' : 'Phase or cluster'}</span>
                <input
                  value={draft.towerOrBlock}
                  placeholder={draft.propertyType === 'apartment' ? 'Example: Tower 7' : 'Example: Phase 2'}
                  onChange={(event) =>
                    setDraft((current) => ({ ...current, towerOrBlock: event.target.value }))
                  }
                />
              </label>
              <label className="field">
                <span>{draft.propertyType === 'apartment' ? 'Flat number *' : 'Villa number *'}</span>
                <input
                  value={draft.unitOrVillaNumber}
                  placeholder={draft.propertyType === 'apartment' ? 'Example: 1204' : 'Example: V-48'}
                  onChange={(event) =>
                    setDraft((current) => ({ ...current, unitOrVillaNumber: event.target.value }))
                  }
                />
              </label>
              <label className="field">
                <span>Inspection stage</span>
                <select
                  value={draft.inspectionStage}
                  onChange={(event) =>
                    setDraft((current) => ({
                      ...current,
                      inspectionStage: event.target.value as PropertyDraft['inspectionStage'],
                    }))
                  }
                >
                  <option value="pre_handover">Pre-handover inspection</option>
                  <option value="final_possession">Final possession visit</option>
                  <option value="post_handover_followup">Post-handover follow-up</option>
                </select>
              </label>
              <label className="field">
                <span>Bedrooms</span>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={draft.bedrooms}
                  onChange={(event) =>
                    setDraft((current) => ({ ...current, bedrooms: Number(event.target.value) }))
                  }
                />
              </label>
              <label className="field">
                <span>Bathrooms</span>
                <input
                  type="number"
                  min="1"
                  max="12"
                  value={draft.bathrooms}
                  onChange={(event) =>
                    setDraft((current) => ({ ...current, bathrooms: Number(event.target.value) }))
                  }
                />
              </label>
            </div>

            <button
              className="button button-primary"
              type="button"
              disabled={!canSave || saveStatus === 'loading'}
              onClick={() => void handleSave()}
            >
              {saveStatus === 'loading'
                ? 'Saving property…'
                : existingPropertyId
                  ? 'Update property'
                  : 'Save and continue'}
            </button>
            <p className="inline-note">
              Manual project entries are stored as pending submissions and never become verified automatically.
            </p>
          </section>
        </main>
      </div>
    );
  }

  return (
    <div className="landing-shell">
      <header className="landing-nav">
        <div className="wordmark">
          <span className="brand-mark" aria-hidden="true">NS</span>
          <strong>NivasaSaathi</strong>
        </div>
        <span className="scope-pill">Free guided inspection</span>
      </header>

      <main className="hero">
        <section className="hero-copy">
          <span className="eyebrow">Apartment and villa handover companion</span>
          <h1>Inspect with a plan. Document what matters. Follow issues through closure.</h1>
          <p>
            A mobile-first guided journey for buyers taking possession of apartments and villas in gated communities. It helps organise observable findings and evidence, without pretending to replace qualified civil, MEP, structural or legal professionals.
          </p>

          {message ? <div className="status-banner dark-banner" role="alert">{message}</div> : null}

          <div className="hero-actions">
            <button
              className="button button-primary"
              type="button"
              disabled={authStatus === 'loading'}
              onClick={() => void startGuestSetup(false)}
            >
              {authStatus === 'loading' ? 'Creating secure guest session…' : 'Start as guest'}
            </button>
            <button
              className="button button-secondary"
              type="button"
              disabled={!savedProperty || authStatus === 'loading'}
              onClick={() => void startGuestSetup(true)}
            >
              Resume saved setup
            </button>
          </div>
          <div className="trust-row" aria-label="Product principles">
            <span>No ChatGPT sign-in</span>
            <span>Basic report remains free</span>
            <span>Phone only for expert help</span>
          </div>
        </section>

        <aside className="product-preview" aria-label="Inspection journey preview">
          <span className="eyebrow">Your guided journey</span>
          <h2>From first check to accountable closure</h2>
          <ol>
            <li><strong>01</strong><span>Set up the property and site conditions</span></li>
            <li><strong>02</strong><span>Complete contextual room and system checks</span></li>
            <li><strong>03</strong><span>Capture issues, notes and evidence</span></li>
            <li><strong>04</strong><span>Create actions with owners and due dates</span></li>
            <li><strong>05</strong><span>Close only with evidence or explicit waiver</span></li>
          </ol>
        </aside>
      </main>
    </div>
  );
}
